import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ShieldCheck, CreditCard, CalendarDays, BarChart, Loader2 } from 'lucide-react';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import DoneForYouModal from '../components/DoneForYouModal';
import VeteranVerificationModal from '../components/VeteranVerificationModal';
import VeteranClaimModal from '../components/VeteranClaimModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PageSEO from '../components/PageSEO';
import axios from 'axios';
import { toast } from 'sonner';

// Custom Accordion for FAQ
const Accordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="w-full max-w-4xl mx-auto space-y-4">
      {faqs.map((faq, idx) => (
        <div key={idx} className="border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-sm transition-all hover:shadow-md">
          <button
            className="w-full flex items-center justify-between p-6 sm:p-8 text-left focus:outline-none"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
          >
            <span className="text-lg font-bold text-[#021426] pr-4">{faq.q}</span>
            <span className={`transform transition-transform duration-300 flex-shrink-0 ${openIndex === idx ? 'rotate-180' : ''}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FEAB3D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </span>
          </button>
          <div className={`transition-all duration-300 ease-in-out ${openIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
            <div className="px-6 sm:px-8 pb-6 sm:pb-8 text-[#4C545B] text-base leading-relaxed">
              {faq.a}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Pricing({ isAuthenticated, user }) {
  const navigate = useNavigate();
  const veteranTriggerRef = useRef(null);
  const [veteranOpen, setVeteranOpen] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [isDoneForYouModalOpen, setIsDoneForYouModalOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleCtaClick = () => navigate('/scan');
  
  const handleCheckout = async (plan) => {
    setCheckoutLoading(plan);
    try {
      if (isAuthenticated) {
        const token = localStorage.getItem('token');
        const res = await axios.post('/create-checkout-session', { plan, is_annual: isAnnual }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = res.data.url;
      } else {
        navigate(`/payment-signup?plan=${plan}&annual=${isAnnual}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initialize checkout.");
      setCheckoutLoading(false);
    }
  };

  const handleDoneForYouClick = () => {
    setIsDoneForYouModalOpen(true);
  };

  const openVeteranVerification = () => {
    setVeteranOpen(true);
  };

  const comparisonData = [
    { feature: 'Price', free: '$0', monitor: '$79/mo', adviser: '$249/mo', custom: 'From $2,500' },
    { feature: 'Websites', free: '1', monitor: '1', adviser: '3', custom: 'Unlimited' },
    { feature: 'AI visibility scorecard', free: 'Yes', monitor: 'Yes', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Questions tracked', free: '3, once', monitor: '25, monthly', adviser: '25, monthly', custom: 'Custom' },
    { feature: 'AI engines covered', free: '4', monitor: '4', adviser: '4', custom: '4+' },
    { feature: 'Technical readiness check', free: 'Yes', monitor: 'Yes', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Prioritized fix list', free: 'Yes', monitor: 'Yes', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Monthly rescan', free: 'No', monitor: 'Yes', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Competitor comparison', free: 'No', monitor: 'Yes', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Visibility drop alerts', free: 'No', monitor: 'Yes', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Report history', free: 'No', monitor: 'Yes', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Fixes implemented for you', free: 'No', monitor: 'No', adviser: '1 per month', custom: 'Full program' },
    { feature: 'Listing & citation buildout', free: 'No', monitor: 'No', adviser: 'Yes', custom: 'Yes' },
    { feature: 'Strategy calls', free: 'No', monitor: 'No', adviser: 'Quarterly', custom: 'Ongoing' },
    { feature: 'Support', free: 'Email', monitor: 'Email', adviser: 'Email + phone', custom: 'Dedicated adviser' },
  ];

  const faqs = [
    { q: 'Is the free audit really free?', a: 'Yes. No account, no credit card, no trial that quietly converts. Enter your website and you get your report.' },
    { q: 'What\'s the difference between Monitor and Adviser?', a: 'Monitor tells you what to fix. Adviser means we fix it. If you have someone who can handle the technical and content work, Monitor is enough. If you don\'t — and most small businesses don\'t — Adviser is the one that actually moves the number.' },
    { q: 'Can I cancel anytime?', a: 'Yes, on any monthly plan. No cancellation fees and no phone call required.' },
    { q: 'Do I need a developer?', a: 'No. Connecting your site takes a few minutes, and on the Adviser plan we handle the technical work ourselves.' },
    { q: 'Will this guarantee ChatGPT recommends my business?', a: 'No, and be wary of anyone who promises that. Nobody controls what an AI model says. What we can do is make sure your business is accessible, accurately described, and well-cited across the sources these systems draw from — which is what actually drives recommendations.' },
    { q: 'How long until I see results?', a: 'Technical fixes register within days to a few weeks. Citation and listing work usually takes 30–90 days to show up in AI answers.' },
    { q: 'What if my business is tiny?', a: 'Then start with the free audit. Plenty of small businesses fix their top three issues themselves and never need to pay us. We\'d rather you do that than buy something you don\'t need.' },
    { q: 'Do you work with agencies?', a: 'Yes. Reseller and white-label arrangements are available under Done For You.' },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://aisearchadvisers.com/#organization",
        "name": "AI Search Advisers",
        "url": "https://aisearchadvisers.com/",
        "description": "AI Search Advisers helps businesses assess and improve website readiness for AI-driven discovery through website audits, AI visibility insights, crawlability analysis, structured data evaluation, and prioritized recommendations.",
        "email": "info@aisearchadvisers.com"
      },
      {
        "@type": "WebSite",
        "@id": "https://aisearchadvisers.com/#website",
        "url": "https://aisearchadvisers.com/",
        "name": "AI Search Advisers",
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": "https://aisearchadvisers.com/pricing#webpage",
        "url": "https://aisearchadvisers.com/pricing",
        "name": "AI Search Audit Pricing & Plans | AI Search Advisers",
        "description": "Explore AI Search Advisers pricing and plans for website audits, AI search visibility insights, and actionable optimization recommendations.",
        "isPartOf": { "@id": "https://aisearchadvisers.com/#website" },
        "about": { "@id": "https://aisearchadvisers.com/#organization" },
        "publisher": { "@id": "https://aisearchadvisers.com/#organization" },
        "inLanguage": "en-US"
      },
      {
        "@type": "Service",
        "name": "AI Search Visibility Services",
        "provider": { "@id": "https://aisearchadvisers.com/#organization" },
        "url": "https://aisearchadvisers.com/pricing",
        "description": "AI Search visibility audits, monitoring, and advisory services to improve your discoverability on ChatGPT, Perplexity, and other AI engines.",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "AI Search Advisers Plans",
          "itemListElement": [
            {
              "@type": "Offer",
              "name": "Free Audit",
              "price": "0",
              "priceCurrency": "USD",
              "url": "https://aisearchadvisers.com/scan"
            },
            {
              "@type": "Offer",
              "name": "Monitor Monthly",
              "price": "79",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "79",
                "priceCurrency": "USD",
                "unitText": "MONTH"
              },
              "url": "https://aisearchadvisers.com/pricing"
            },
            {
              "@type": "Offer",
              "name": "Monitor Annual",
              "price": "790",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "790",
                "priceCurrency": "USD",
                "unitText": "YEAR"
              },
              "url": "https://aisearchadvisers.com/pricing"
            },
            {
              "@type": "Offer",
              "name": "Adviser Monthly",
              "price": "249",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "249",
                "priceCurrency": "USD",
                "unitText": "MONTH"
              },
              "url": "https://aisearchadvisers.com/pricing"
            },
            {
              "@type": "Offer",
              "name": "Adviser Annual",
              "price": "2490",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "2490",
                "priceCurrency": "USD",
                "unitText": "YEAR"
              },
              "url": "https://aisearchadvisers.com/pricing"
            }
          ]
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-sans text-[#021426] flex flex-col selection:bg-[#FEAB3D]/30 selection:text-[#021426]">
      <PageSEO 
        title="AI Search Audit Pricing & Plans | AI Search Advisers"
        description="Explore AI Search Advisers pricing and plans for website audits, AI search visibility insights, and actionable optimization recommendations."
        canonical="https://aisearchadvisers.com/pricing"
        schema={schema}
      />
      <GlobalHeader />

      {/* SECTION 1 — PREMIUM PRICING HERO */}
      <section className="pt-40 pb-20 px-6 text-center relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[500px] bg-[#FEAB3D] opacity-[0.04] blur-[120px] rounded-full pointer-events-none" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="inline-block text-[#FEAB3D] font-bold tracking-[0.2em] uppercase text-xs mb-6 px-4 py-1.5 bg-[#FEAB3D]/10 rounded-full border border-[#FEAB3D]/20">
            PRICING
          </div>
          <h1 className="font-['Fraunces',serif] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight text-[#021426] leading-[1.1]">
            Simple pricing.<br/>Start free.
          </h1>
          <p className="text-[#4C545B] text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Find out where you stand in AI search at no cost. Upgrade only when you want us to help you fix it.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-sm font-medium text-[#4C545B]">
            <div className="flex items-center gap-2"><Check className="w-5 h-5 text-[#FEAB3D]" /> Start free</div>
            <div className="flex items-center gap-2"><Check className="w-5 h-5 text-[#FEAB3D]" /> No credit card for the audit</div>
            <div className="flex items-center gap-2"><Check className="w-5 h-5 text-[#FEAB3D]" /> Cancel monthly plans anytime</div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — PRICING CARDS */}
      <section className="px-6 pb-16 relative z-20">
        <div className="max-w-[1200px] mx-auto">
          {/* Toggle */}
          <div className="flex justify-center items-center gap-4 mb-16">
            <span className={`text-sm font-semibold transition-colors ${!isAnnual ? 'text-[#021426]' : 'text-slate-400'}`}>Monthly</span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative inline-flex h-8 w-16 items-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#FEAB3D] focus:ring-offset-2 rounded-full ${isAnnual ? 'bg-[#FEAB3D]' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-6 w-6 transform bg-white rounded-full shadow-sm transition-transform duration-300 ${isAnnual ? 'translate-x-9' : 'translate-x-1'}`} />
            </button>
            <span className={`text-sm font-semibold transition-colors flex items-center ${isAnnual ? 'text-[#021426]' : 'text-slate-400'}`}>
              Annually
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8 items-stretch">
            
            {/* CARD 1: FREE AUDIT */}
            <div className="flex flex-col bg-white border border-[#e5e9ee] rounded-[24px] p-8 lg:p-10 relative shadow-sm hover:shadow-xl transition-all duration-300 group">
              <div className="mb-8">
                <div className="font-['Fraunces',serif] text-xl sm:text-2xl font-bold text-[#021426] mb-1">Free Audit</div>
                <div className="text-xs font-semibold text-[#4C545B] tracking-wider uppercase">One-time audit</div>
              </div>
              <div className="mb-6">
                <div className="font-['Fraunces',serif] text-4xl sm:text-5xl font-bold text-[#021426]">$0</div>
              </div>
              <div className="text-sm text-[#4C545B] mb-8 leading-relaxed h-10">One-time visibility check. No account, no card.</div>
              <div className="h-px w-full bg-slate-100 mb-8" />
              <ul className="space-y-4 mb-10 flex-grow">
                {['Full AI visibility scorecard for one website', '3 real questions tested across major AI engines', 'Technical readiness check', 'Business listing and citation review', 'Prioritized list of what to fix first', 'Shareable report delivered to your inbox'].map((f, i) => (
                  <li key={i} className="flex items-start text-sm text-[#021426] leading-snug">
                    <Check className="w-5 h-5 text-[#FEAB3D] shrink-0 mr-3" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <Button onClick={handleCtaClick} className="w-full py-7 rounded-xl font-bold text-base bg-[#021426] text-white hover:bg-[#0A4378] transition-all shadow-md hover:shadow-lg">
                  Run my free audit
                </Button>
                <div className="text-center text-xs text-slate-500 mt-4 font-medium">Takes about 60 seconds.</div>
              </div>
            </div>

            {/* CARD 2: MONITOR */}
            <div className="flex flex-col bg-white border border-[#e5e9ee] rounded-[24px] p-8 lg:p-10 relative shadow-sm hover:shadow-xl transition-all duration-300 group">
              <div className="mb-8">
                <div className="font-['Fraunces',serif] text-xl sm:text-2xl font-bold text-[#021426] mb-1">Monitor</div>
                <div className="text-xs font-semibold text-transparent select-none">Spacer</div>
              </div>
              <div className="mb-6 flex flex-col justify-end h-[60px]">
                <div className="flex items-baseline">
                  <span className="font-['Fraunces',serif] text-4xl sm:text-5xl font-bold text-[#021426]">${isAnnual ? '790' : '79'}</span>
                  <span className="text-[#4C545B] font-medium ml-2">{isAnnual ? '/year' : '/month'}</span>
                </div>
                {isAnnual && <div className="text-[#FEAB3D] font-bold text-xs mt-2 uppercase tracking-wide bg-[#FEAB3D]/10 inline-block px-2 py-0.5 rounded self-start">Save 2 months</div>}
              </div>
              <div className="text-sm text-[#4C545B] mb-8 leading-relaxed h-10">For businesses that want to know when things change.</div>
              <div className="h-px w-full bg-slate-100 mb-8" />
              <ul className="space-y-4 mb-10 flex-grow">
                {['Monthly automated visibility rescan', '25 questions tracked across ChatGPT, Perplexity, Gemini, and Claude', 'Competitor comparison', 'Visibility drop alerts', 'Full report history', 'Email support'].map((f, i) => (
                  <li key={i} className="flex items-start text-sm text-[#021426] leading-snug">
                    <Check className="w-5 h-5 text-[#FEAB3D] shrink-0 mr-3" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <Button 
                  onClick={() => handleCheckout('monitor')} 
                  disabled={checkoutLoading === 'monitor'}
                  className="w-full py-7 rounded-xl font-bold text-base bg-[#021426] text-white hover:bg-[#0A4378] transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {checkoutLoading === 'monitor' ? (
                    <span className="flex items-center gap-2 justify-center">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Opening checkout...
                    </span>
                  ) : "Start monitoring"}
                </Button>
                <div className="text-center text-xs text-slate-500 mt-4 font-medium">Cancel anytime.</div>
              </div>
            </div>

            {/* CARD 3: ADVISER (MOST POPULAR) */}
            <div className="flex flex-col bg-[#021426] border-2 border-[#FEAB3D] rounded-[24px] p-8 lg:p-10 relative shadow-[0_12px_40px_rgba(254,171,61,0.25)] lg:scale-105 z-10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FEAB3D] text-[#021426] text-xs font-extrabold uppercase tracking-widest px-5 py-2 rounded-full whitespace-nowrap shadow-md">
                MOST POPULAR
              </div>
              <div className="mb-8 pt-2">
                <div className="font-['Fraunces',serif] text-xl sm:text-2xl font-bold text-white mb-1">Adviser</div>
                <div className="text-xs font-semibold text-transparent select-none">Spacer</div>
              </div>
              <div className="mb-6 flex flex-col justify-end h-[60px]">
                <div className="flex items-baseline">
                  <span className="font-['Fraunces',serif] text-4xl sm:text-5xl font-bold text-white">${isAnnual ? '2,490' : '249'}</span>
                  <span className="text-[rgba(255,255,255,0.6)] font-medium ml-2">{isAnnual ? '/year' : '/month'}</span>
                </div>
                {isAnnual && <div className="text-[#021426] font-bold text-xs mt-2 uppercase tracking-wide bg-[#FEAB3D] inline-block px-2 py-0.5 rounded self-start">Save 2 months</div>}
              </div>
              <div className="text-sm text-[rgba(255,255,255,0.7)] mb-8 leading-relaxed h-10">For businesses that want the problems actually fixed.</div>
              <div className="h-px w-full bg-white/10 mb-8" />
              <div className="text-xs font-bold text-[#FEAB3D] uppercase tracking-wider mb-4">Monitoring tells you what's wrong. Adviser helps fix it.</div>
              <ul className="space-y-4 mb-10 flex-grow">
                {['Everything in Monitor', 'One improvement implemented for you each month', 'Content, structured data, technical fixes, or listings', 'Business listing and citation buildout', 'Quarterly strategy call with an adviser', 'Direct email and phone access', 'Up to 3 websites'].map((f, i) => (
                  <li key={i} className="flex items-start text-sm text-white leading-snug">
                    <Check className="w-5 h-5 text-[#FEAB3D] shrink-0 mr-3" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <Button 
                  onClick={() => handleCheckout('adviser')} 
                  disabled={checkoutLoading === 'adviser'}
                  className="w-full py-7 rounded-xl font-bold text-base bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] transition-all shadow-[0_4px_14px_rgba(254,171,61,0.4)] hover:shadow-[0_6px_20px_rgba(254,171,61,0.5)] hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {checkoutLoading === 'adviser' ? (
                    <span className="flex items-center gap-2 justify-center">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Opening checkout...
                    </span>
                  ) : "Get started"}
                </Button>
                <div className="text-center text-xs text-[rgba(255,255,255,0.5)] mt-4 font-medium flex flex-col gap-1">
                  <span>Cancel anytime.</span>
                  <span>Most customers see movement within 60 days.</span>
                </div>
              </div>
            </div>

          </div>
          
          {/* CARD 4: DONE FOR YOU */}
          <div className="mt-8 flex flex-col lg:flex-row items-start lg:items-center justify-between bg-white border border-[#e5e9ee] rounded-[24px] p-8 lg:p-10 relative shadow-sm hover:shadow-xl transition-all duration-300 gap-8">
            <div className="flex-1">
              <div className="mb-3">
                <span className="text-[#FEAB3D] font-bold text-xs uppercase tracking-widest">Need a hands-on partner?</span>
              </div>
              <div className="flex items-baseline gap-4 mb-4">
                <div className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold text-[#021426]">Done For You</div>
                <div className="text-[#4C545B] font-medium text-sm">from $2,500</div>
              </div>
              <div className="text-sm text-[#4C545B] mb-6 leading-relaxed max-w-2xl">
                For businesses that need to catch up fast, or that have more than a few sites. Complete technical and content remediation, with a dedicated adviser.
              </div>
              <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
                {['Project-based, with ongoing support available', 'Complete technical and content remediation', 'Full entity and citation infrastructure buildout', 'Custom content program built for AI citation', 'Multi-location and multi-site coverage', 'Dedicated adviser', 'Agency and reseller arrangements available'].map((f, i) => (
                  <li key={i} className="flex items-start text-sm text-[#021426] leading-snug">
                    <Check className="w-5 h-5 text-[#FEAB3D] shrink-0 mr-3" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-auto shrink-0 flex flex-col items-center justify-center pt-6 lg:pt-0 border-t lg:border-t-0 border-[#e5e9ee] lg:border-l lg:pl-10">
              <Button onClick={handleDoneForYouClick} className="w-full lg:w-48 py-7 rounded-xl font-bold text-base bg-transparent text-[#021426] border-2 border-[#021426] hover:bg-[#021426]/5 transition-all">
                Talk to us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — QUICK TRUST ROW */}
      <section className="px-6 py-12 lg:py-16">
        <div className="max-w-[1000px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-[#e5e9ee] shadow-sm text-[#0C5B94]">
              <CalendarDays className="w-5 h-5" />
            </div>
            <span className="text-sm font-semibold text-[#021426]">No long-term contract</span>
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-[#e5e9ee] shadow-sm text-[#0C5B94]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path><line x1="12" y1="2" x2="12" y2="12"></line></svg>
            </div>
            <span className="text-sm font-semibold text-[#021426]">Cancel monthly plans anytime</span>
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-[#e5e9ee] shadow-sm text-[#0C5B94]">
              <CreditCard className="w-5 h-5" />
            </div>
            <span className="text-sm font-semibold text-[#021426]">Free audit requires no credit card</span>
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-[#e5e9ee] shadow-sm text-[#0C5B94]">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <span className="text-sm font-semibold text-[#021426]">Built for businesses, not SEO experts</span>
          </div>
        </div>
      </section>

      {/* SECTION 4 — VETERAN-OWNED BUSINESSES */}
      <section className="px-6 py-20 lg:py-28">
        <div className="max-w-[1000px] mx-auto bg-[#05192E] text-white rounded-[32px] overflow-hidden shadow-2xl flex flex-col md:flex-row border border-[rgba(255,255,255,0.08)]">
          <div className="p-10 md:p-16 md:w-3/5 flex flex-col justify-center relative">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#0C5B94]/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <div className="inline-block text-[#FEAB3D] font-bold tracking-[0.2em] uppercase text-xs mb-6">Support</div>
              <h3 className="font-['Fraunces',serif] text-3xl sm:text-4xl font-bold mb-4 leading-tight">Veteran-owned businesses</h3>
              <div className="text-[#FEAB3D] font-semibold text-lg sm:text-xl mb-6">20% off any plan, for as long as you're a customer.</div>
              <p className="text-[rgba(255,255,255,0.75)] text-base leading-relaxed mb-10">
                We got our start serving veteran-owned businesses and we're not about to stop. Verified veteran-owned businesses get 20% off every plan — and your certifications are an AI visibility asset most owners aren't using. We'll show you how.
              </p>
              <Button type="button" ref={veteranTriggerRef} onClick={openVeteranVerification} className="self-start bg-transparent border border-[rgba(255,255,255,0.3)] hover:bg-white hover:text-[#021426] hover:border-white text-white px-8 py-6 rounded-xl font-bold text-base transition-all">
                Claim veteran pricing
              </Button>
            </div>
          </div>
          <div className="bg-[#FEAB3D] md:w-2/5 p-12 flex flex-col items-center justify-center text-center relative overflow-hidden">
            <div className="absolute -right-10 -bottom-10 opacity-10">
              <ShieldCheck className="w-64 h-64 text-[#021426]" />
            </div>
            <div className="font-['Fraunces',serif] text-7xl lg:text-8xl font-black text-[#021426] leading-none mb-2 relative z-10 tracking-tighter">20%</div>
            <div className="text-xl font-bold text-[#021426] tracking-[0.2em] uppercase relative z-10">Off</div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — PLAN COMPARISON */}
      <section className="px-6 py-20 lg:py-28 bg-white border-y border-[#e5e9ee]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#021426] mb-6">Compare plans</h2>
            <p className="text-[#4C545B] text-lg max-w-2xl mx-auto">
              Choose how much visibility, monitoring and hands-on support you need.
            </p>
          </div>
          
          <div className="overflow-x-auto rounded-[24px] border border-[#e5e9ee] shadow-sm">
            <table className="w-full min-w-[900px] text-left border-collapse">
              <thead>
                <tr>
                  <th className="p-6 border-b border-[#e5e9ee] bg-[#FAF7F2] font-semibold text-[#021426] sticky left-0 z-10 w-1/4">Feature</th>
                  <th className="p-6 border-b border-[#e5e9ee] bg-[#FAF7F2] font-bold text-[#021426] text-center w-[18%]">Free Audit</th>
                  <th className="p-6 border-b border-[#e5e9ee] bg-[#FAF7F2] font-bold text-[#021426] text-center w-[18%]">Monitor</th>
                  <th className="p-6 border-b border-[#e5e9ee] bg-[#FEAB3D]/10 font-bold text-[#021426] text-center w-[18%] border-x-2 border-t-2 border-[#FEAB3D] relative shadow-[0_-4px_0_#FEAB3D_inset]">
                    Adviser
                  </th>
                  <th className="p-6 border-b border-[#e5e9ee] bg-[#FAF7F2] font-bold text-[#021426] text-center w-[18%]">Done For You</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className={`group ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                    <td className={`p-5 border-b border-slate-100 font-medium text-[#4C545B] sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white group-hover:bg-slate-50' : 'bg-slate-50/50 group-hover:bg-slate-100/50'}`}>
                      {row.feature}
                    </td>
                    <td className="p-5 border-b border-slate-100 text-center text-sm font-medium text-[#021426]">{row.free === 'Yes' ? <Check className="w-5 h-5 mx-auto text-[#11554D]" /> : row.free === 'No' ? <span className="text-slate-300">—</span> : row.free}</td>
                    <td className="p-5 border-b border-slate-100 text-center text-sm font-medium text-[#021426]">{row.monitor === 'Yes' ? <Check className="w-5 h-5 mx-auto text-[#11554D]" /> : row.monitor === 'No' ? <span className="text-slate-300">—</span> : row.monitor}</td>
                    <td className="p-5 border-b border-slate-100 text-center text-sm font-bold text-[#021426] border-x-2 border-[#FEAB3D] bg-[#FEAB3D]/5">
                      {row.adviser === 'Yes' ? <Check className="w-5 h-5 mx-auto text-[#FEAB3D]" /> : row.adviser === 'No' ? <span className="text-[#FEAB3D]/40">—</span> : row.adviser}
                    </td>
                    <td className="p-5 border-b border-slate-100 text-center text-sm font-medium text-[#021426]">{row.custom === 'Yes' ? <Check className="w-5 h-5 mx-auto text-[#11554D]" /> : row.custom === 'No' ? <span className="text-slate-300">—</span> : row.custom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SECTION 6 — VALUE EXPLANATION */}
      <section className="px-6 py-20 lg:py-28 bg-[#FAF7F2]">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-['Fraunces',serif] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#021426] mb-6 leading-tight">
              Monitoring tells you what changed.<br/><span className="text-[#0C5B94]">Adviser helps you fix it.</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {/* Monitor Block */}
            <div className="bg-white rounded-[32px] p-10 border border-[#e5e9ee] shadow-sm">
              <div className="font-['Fraunces',serif] font-bold text-2xl text-[#021426] mb-3">Monitor</div>
              <p className="text-[#4C545B] text-sm font-medium mb-10 h-10">Best for teams that can handle their own implementation.</p>
              <ul className="space-y-5">
                <li className="flex items-center gap-4 font-semibold text-[#021426]"><BarChart className="w-6 h-6 text-slate-300" /> Track visibility</li>
                <li className="flex items-center gap-4 font-semibold text-[#021426]"><BarChart className="w-6 h-6 text-slate-300" /> Watch competitors</li>
                <li className="flex items-center gap-4 font-semibold text-[#021426]"><BarChart className="w-6 h-6 text-slate-300" /> Spot drops</li>
                <li className="flex items-center gap-4 font-semibold text-[#021426]"><BarChart className="w-6 h-6 text-slate-300" /> Review progress</li>
              </ul>
            </div>
            
            {/* Adviser Block */}
            <div className="bg-[#021426] rounded-[32px] p-10 border-2 border-[#FEAB3D] shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#FEAB3D] blur-[100px] opacity-20 pointer-events-none" />
              <div className="font-['Fraunces',serif] font-bold text-2xl text-white mb-3 relative z-10">Adviser</div>
              <p className="text-[rgba(255,255,255,0.7)] text-sm font-medium mb-10 h-10 relative z-10">Best for businesses that want hands-on help.</p>
              <ul className="space-y-5 relative z-10">
                <li className="flex items-center gap-4 font-semibold text-white"><Check className="w-6 h-6 text-[#FEAB3D]" /> Everything in Monitor</li>
                <li className="flex items-center gap-4 font-semibold text-white"><Check className="w-6 h-6 text-[#FEAB3D]" /> Priority fixes implemented</li>
                <li className="flex items-center gap-4 font-semibold text-white"><Check className="w-6 h-6 text-[#FEAB3D]" /> Citation/listing improvements</li>
                <li className="flex items-center gap-4 font-semibold text-white"><Check className="w-6 h-6 text-[#FEAB3D]" /> Strategy support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7 — FAQ */}
      <section className="px-6 py-20 lg:py-28 bg-white border-t border-[#e5e9ee]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#021426]">Frequently asked questions</h2>
          </div>
          <Accordion faqs={faqs} />
        </div>
      </section>

      {/* SECTION 8 — FINAL CONVERSION CTA */}
      <section className="px-6 pb-32 bg-white pt-10">
        <div className="max-w-[1000px] mx-auto bg-[#021426] rounded-[32px] p-12 sm:p-20 text-center relative overflow-hidden shadow-2xl border border-[rgba(255,255,255,0.1)]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0C5B94]/40 via-transparent to-[#FEAB3D]/10 pointer-events-none" />
          <div className="relative z-10">
            <h2 className="font-['Fraunces',serif] text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">Not sure where to start?</h2>
            <p className="text-[rgba(255,255,255,0.8)] text-lg sm:text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
              Run the free audit. It takes about a minute, costs nothing, and you'll know more about how AI sees your business than most of your competitors know about theirs.
            </p>
            <div className="flex flex-col items-center justify-center gap-4">
              <Button onClick={handleCtaClick} className="bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] px-10 py-7 text-lg font-bold rounded-xl shadow-[0_8px_32px_rgba(254,171,61,0.3)] transition-all hover:-translate-y-1">
                Run my free audit
              </Button>
              <span className="text-[rgba(255,255,255,0.5)] text-sm font-medium">No credit card required.</span>
            </div>
          </div>
        </div>
      </section>

      <GlobalFooter />

      <VeteranVerificationModal open={veteranOpen} onOpenChange={setVeteranOpen} triggerRef={veteranTriggerRef} />
      <VeteranClaimModal isAuthenticated={isAuthenticated} user={user} />

      {/* Done For You Modal */}
      <DoneForYouModal 
        isOpen={isDoneForYouModalOpen} 
        onClose={() => setIsDoneForYouModalOpen(false)} 
      />
    </div>
  );
}

